import { Link2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getClient, linkClient, listClients, unlinkClient } from "@/features/clients/api";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import type { Client, ClientLink, EmployerBelongsTo } from "@/features/clients/types";
import { ApiError } from "@/lib/api";

interface ClientLinksSectionProps {
  client: Client;
  /** Push the refreshed client up so the profile reflects the new links. */
  onClientUpdated: (client: Client) => void;
}

/**
 * The sole-proprietorship / individual cross-link collection (legacy
 * `link()`/`unlink()`). A Business links to the Individual who owns it and vice
 * versa; each client manages the links it created. Links join opposite types in
 * the same firm — the server derives the link type and enforces both rules.
 */
export function ClientLinksSection({ client, onClientUpdated }: ClientLinksSectionProps) {
  const isBusiness = client.type === "Business";
  // A business links to an individual (its owner); an individual links to a business.
  const targetType = isBusiness ? "individual" : "business";
  const targetNoun = isBusiness ? "Individual" : "Business";
  const links = client.links ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [removing, setRemoving] = useState<ClientLink | null>(null);

  async function refresh() {
    const fresh = await getClient(client.id);
    onClientUpdated(fresh);
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex items-center justify-between border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <Link2 className="text-primary size-5" /> Linked {targetNoun}s
        </h3>
        <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="size-4" /> Link {targetNoun}
        </Button>
      </div>

      {links.length === 0 ? (
        <p className="text-muted-foreground px-6 py-8 text-sm">
          No linked {targetNoun.toLowerCase()}s yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{targetNoun}</TableHead>
              <TableHead>Relationship</TableHead>
              <TableHead>Belongs To</TableHead>
              <TableHead className="w-16 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.map((link) => (
              <TableRow key={link.id}>
                <TableCell className="font-medium">{link.toClient.name}</TableCell>
                <TableCell>
                  {link.linkType === "SOLE_PROP" ? "Sole proprietorship" : "Individual"}
                </TableCell>
                <TableCell>{link.belongsTo === "SPOUSE" ? "Spouse" : "Taxpayer"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Unlink ${link.toClient.name}`}
                    onClick={() => setRemoving(link)}
                  >
                    <Trash2 className="text-destructive size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* key remounts on open so the picker starts fresh without resetting
          state inside an effect. */}
      <LinkDialog
        key={dialogOpen ? "open" : "closed"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientId={client.id}
        targetType={targetType}
        targetNoun={targetNoun}
        askBelongsTo={!isBusiness}
        existingIds={links.map((l) => l.toClient.id)}
        onLinked={refresh}
      />

      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove link?</AlertDialogTitle>
            <AlertDialogDescription>
              This unlinks {removing?.toClient.name} from this client. Neither client is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!removing) return;
                try {
                  await unlinkClient(client.id, removing.toClient.id);
                  toast.success("Link removed");
                  await refresh();
                } catch (err) {
                  toast.error(err instanceof ApiError ? err.message : "Couldn't remove link.");
                } finally {
                  setRemoving(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

interface LinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  targetType: "individual" | "business";
  targetNoun: string;
  askBelongsTo: boolean;
  existingIds: string[];
  onLinked: () => Promise<void>;
}

/** Picks an opposite-type client (and, for individuals, taxpayer/spouse) to link. */
function LinkDialog({
  open,
  onOpenChange,
  clientId,
  targetType,
  targetNoun,
  askBelongsTo,
  existingIds,
  onLinked,
}: LinkDialogProps) {
  const [candidates, setCandidates] = useState<Client[] | null>(null);
  const [toClient, setToClient] = useState("");
  const [belongsTo, setBelongsTo] = useState<EmployerBelongsTo>("TAXPAYER");
  const [saving, setSaving] = useState(false);

  // Stable primitive so the effect doesn't re-run on every parent render just
  // because `existingIds` is a fresh array reference.
  const existingKey = existingIds.join(",");
  useEffect(() => {
    if (!open) return;
    let active = true;
    const alreadyLinked = new Set(existingKey ? existingKey.split(",") : []);
    // One max-size page covers a firm's roster for a picker; not linked already.
    listClients({ type: targetType, pageSize: 100 })
      .then((res) => {
        if (active) setCandidates(res.clients.filter((c) => !alreadyLinked.has(c.id)));
      })
      .catch(() => {
        if (active) setCandidates([]);
      });
    return () => {
      active = false;
    };
  }, [open, targetType, existingKey]);

  async function submit() {
    if (!toClient) return;
    setSaving(true);
    try {
      await linkClient(clientId, {
        to_client: toClient,
        belongs_to: askBelongsTo ? belongsTo : "TAXPAYER",
      });
      toast.success(`${targetNoun} linked`);
      onOpenChange(false);
      await onLinked();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't create link.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link {targetNoun}</DialogTitle>
        </DialogHeader>

        {candidates === null ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : candidates.length === 0 ? (
          <p className="text-muted-foreground py-6 text-sm">
            No {targetNoun.toLowerCase()} clients are available to link.
          </p>
        ) : (
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label className={FIELD_LABEL}>{targetNoun}</Label>
              <Select value={toClient || undefined} onValueChange={setToClient}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={`Select a ${targetNoun.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {askBelongsTo && (
              <div className="grid gap-2">
                <Label className={FIELD_LABEL}>Belongs To</Label>
                <Select
                  value={belongsTo}
                  onValueChange={(v) => setBelongsTo(v as EmployerBelongsTo)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TAXPAYER">Taxpayer</SelectItem>
                    <SelectItem value="SPOUSE">Spouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !toClient}>
            {saving ? "Linking…" : "Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
